import { imagineStore } from "@/stores/imagine.store";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { createImagineResumableChat } from "@/hooks/imagine-resumable-chat/create-imagine-resumable-chat";
import * as Sentry from "@sentry/react";
import type { ImagineChat } from "@/hooks/imagine-resumable-chat/imagine-chat";
import type {
  ImagineUIDataParts,
  ImagineUIMessage,
} from "@imagine/ai-service/shared-types";
import { useSubmitChatMessageMutation } from "@/hooks/use-submit-chat-message-mutation";
import { GetProjectResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { imagineConfigStore } from "@/web-components";

export type SendMessageResult = {
  success: boolean;
  projectId: string;
  newProject: GetProjectResponseBody | null;
};

interface ImagineChatContextValue {
  _sendMessage: ({
    projectId,
    message,
  }: {
    projectId: string;
    message: ImagineUIMessage;
  }) => Promise<SendMessageResult>;
  setHistory: (history: Array<ImagineUIMessage>) => void;
  isGenerating: boolean;
  status: ImagineChatType["status"];
  error: ImagineChatType["error"];
  messages: Array<ImagineUIMessage>;
  stop: () => void;
  resumeStream: ({ projectId }: { projectId: string }) => Promise<void>;
  clearError: () => void;
  regenerate: ({ projectId }: { projectId: string }) => Promise<void>;
  reset: () => void;
}

const ImagineChatContext = createContext<ImagineChatContextValue | undefined>(
  undefined,
);

type ImagineChatType = ImagineChat<ImagineUIMessage>;

const onData$ = imagineStore.getState().chat.onData$;
const onFinish$ = imagineStore.getState().chat.onFinish$;
const onError$ = imagineStore.getState().chat.onError$;

const setHistory = imagineStore.getState().chat.setHistory;
const _setSubmittedUserMessage =
  imagineStore.getState().chat.setSubmittedUserMessage;
const _setCurrentAssistantMessage =
  imagineStore.getState().chat.setCurrentAssistantMessage;
const _setError = imagineStore.getState().chat.setError;
const _setStatus = imagineStore.getState().chat.setStatus;
const _syncHistoryAfterGeneration =
  imagineStore.getState().chat.syncHistoryAfterGeneration;

export function ImagineChatProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const history = imagineStore.use.chat.history();
  const submittedUserMessage = imagineStore.use.chat.submittedUserMessage();
  const currentAssistantMessage =
    imagineStore.use.chat.currentAssistantMessage();
  const status = imagineStore.use.chat.status();
  const isGenerating = imagineStore.use.chat.isGenerating();
  const error = imagineStore.use.chat.error();
  const _chatRef = useRef<ImagineChatType | null>(null);
  const submitChatMessageMutation = useSubmitChatMessageMutation({
    onError: (error) => {
      _setError(error as Error);
      _setStatus("error");
    },
  });
  const refreshIframe = imagineStore.use.previewState.refreshIframe();
  const resetWorkspaceState = imagineStore.use.workspaceState.reset();
  const setUIMode = imagineStore.use.ui.setMode();
  const resetTodos = imagineStore.use.chat.resetTodos();
  const setTodos = imagineStore.use.chat.setTodos();
  const isResumingStream = useRef(false);
  const lastSubmittedMessage = useRef<ImagineUIMessage | null>(null);

  const _sendMessage = useCallback(
    async ({
      projectId,
      message,
    }: {
      projectId: string;
      message: ImagineUIMessage;
    }) => {
      lastSubmittedMessage.current = { ...message };
      _setSubmittedUserMessage(message);
      _setStatus("submitted");
      _setError(undefined);

      const currentUrl = new URL(window.location.href);
      const promptLabel = currentUrl.searchParams.get("promptLabel");

      return await submitChatMessageMutation.mutateAsync({
        projectId,
        message,
        promptLabel: promptLabel ?? undefined,
      });
    },
    [submitChatMessageMutation],
  );

  const resumeStream = useCallback(
    async ({ projectId }: { projectId: string }) => {
      if (isResumingStream.current) return;
      _chatRef.current = createImagineResumableChat({
        projectId,
        onData: (dataPart) => {
          onData$.next(dataPart);

          if (dataPart.type === "data-submitted-user-message") {
            _setSubmittedUserMessage(dataPart.data as ImagineUIMessage);
          }

          // if (dataPart.type === "data-workspace-state") {
          //   const data =
          //     dataPart.data as unknown as ImagineUIDataParts["workspace-state"];
          //   updateWorkspaceState(data);

          //   if (data.previewUrl) {
          //     setPreviewUrl(new URL(data.previewUrl), true);
          //   }
          // }

          if (dataPart.type === "data-generation-complete") {
            // resetWorkspaceState();
            setUIMode("preview");
            resetTodos();
            // Defer iframe refresh to next tick to let stream finish processing current batch
            // setTimeout(() => {
            //   refreshIframe();
            // }, 0);
          }

          if (dataPart.type === "data-plan") {
            const plan = dataPart.data as unknown as ImagineUIDataParts["plan"];
            setTodos(plan.items);
          }
        },
        onFinish: (data) => {
          console.log("ON FINISH", data);
          onFinish$.next(data);
          if (!data.isError) {
            _setStatus("ready");
            _syncHistoryAfterGeneration();
          }
          isResumingStream.current = false;
        },
        onError: (error) => {
          // Ignore orphan text-delta errors from AI SDK - these happen on stream replay
          // when text-start chunk was missed
          if (
            error instanceof TypeError &&
            error.message.includes("reading 'text'")
          ) {
            console.warn("Ignoring orphan text-delta error:", error.message);
            return;
          }
          Sentry.captureException(error);
          onError$.next(error);
          _setStatus("error");
          _setError(error as Error);
          imagineConfigStore.getState().setError(error as Error);
        },
        onAssistantMessageChange: (message) => {
          _setCurrentAssistantMessage(message);
        },
      });
      _setStatus("streaming");
      isResumingStream.current = true;
      await _chatRef.current!.resumeStream();
      isResumingStream.current = false;
    },
    [],
  );

  const messages = useMemo(() => {
    const result = [
      ...(history
        ? dedupeHistoryMessages(
            history,
            submittedUserMessage,
            currentAssistantMessage,
          )
        : []),
      submittedUserMessage,
      currentAssistantMessage,
    ].filter(Boolean) as Array<ImagineUIMessage>;
    return result;
  }, [history, submittedUserMessage, currentAssistantMessage]);

  const regenerate = async ({ projectId }: { projectId: string }) => {
    if (!lastSubmittedMessage.current) return;
    await _sendMessage({
      projectId,
      message: lastSubmittedMessage.current,
    });
  };

  const stop = useCallback(() => {
    _chatRef.current!.stop();
  }, []);

  const clearError = useCallback(() => {
    imagineConfigStore.getState().setError(null);
    _setError(undefined);
    _setStatus("ready");
  }, []);

  const reset = useCallback(() => {
    imagineConfigStore.getState().setError(null);
    _setStatus("ready");
    _setError(undefined);
    _setSubmittedUserMessage(null);
    _setCurrentAssistantMessage(null);
    setHistory(null);
  }, []);

  const contextValue: ImagineChatContextValue = {
    _sendMessage,
    setHistory,
    messages,
    isGenerating,
    status,
    error,
    clearError,
    regenerate,
    stop,
    resumeStream,
    reset,
  };

  return (
    <ImagineChatContext.Provider value={contextValue}>
      {children}
    </ImagineChatContext.Provider>
  );
}

export const useImagineChat = () => {
  const context = useContext(ImagineChatContext);
  if (context === undefined) {
    throw new Error("useImagineChat must be used within a ImagineChatProvider");
  }
  return context;
};

function dedupeHistoryMessages(
  history: Array<ImagineUIMessage>,
  currentAssistantMessage: ImagineUIMessage | null,
  submittedUserMessage: ImagineUIMessage | null,
) {
  const result = [...history];

  // If currentAssistantMessage is not null, and it's also present in the history, remove it from the history
  if (currentAssistantMessage) {
    const idx = result.findIndex(
      (message) => message.id === currentAssistantMessage.id,
    );
    if (idx !== -1) {
      result.splice(idx, 1);
    }
  }

  // If submittedUserMessage is not null, and it's also present in the history, remove it from the history
  if (submittedUserMessage) {
    const idx = result.findIndex(
      (message) => message.id === submittedUserMessage.id,
    );
    if (idx !== -1) {
      result.splice(idx, 1);
    }
  }

  return result;
}
